/**
 * Meta Pixel — SCOPED to the /canada-company-india landing page family.
 *
 * Why scoped? The user wants Pixel attribution isolated to the India campaign
 * only; other pages must not talk to this dataset.
 *
 * Behaviour:
 *   - Reads `meta_pixel_id` from server-side settings (never hard-coded).
 *   - Injects the Pixel bootstrap once per browser session on first mount.
 *   - Fires `fbq('track','PageView')` on every mount (so SPA route transitions
 *     between /canada-company-india and /canada-company-india/thank-you each
 *     count as a page_view, as Meta expects).
 *   - Fires NO Lead events. Lead is fired explicitly by the thank-you page
 *     after a successful qualification-form submission via firePixelEvent().
 */
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useSettings } from "@/lib/settings";

const META_PIXEL_RE = /^\d{8,20}$/;
const SCRIPT_ID = "fb-pixel";

export function ensurePixelInjected(pixelId) {
  if (typeof document === "undefined") return false;
  if (!pixelId || !META_PIXEL_RE.test(pixelId)) return false;
  if (document.getElementById(SCRIPT_ID)) return true;
  const s = document.createElement("script");
  s.id = SCRIPT_ID;
  s.text = `
    !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
    n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,
    document,'script','https://connect.facebook.net/en_US/fbevents.js');
    fbq('init', '${pixelId}');
  `;
  document.head.appendChild(s);
  return true;
}

/**
 * Fire a Meta standard event. Idempotent — the caller is responsible for
 * their own single-fire guard. Returns true if dispatched.
 */
export function firePixelEvent(pixelId, eventName, params = {}) {
  if (!ensurePixelInjected(pixelId)) return false;
  if (typeof window === "undefined" || typeof window.fbq !== "function") return false;
  window.fbq("track", eventName, params);
  return true;
}

export default function MetaPixel() {
  const settings = useSettings();
  const { pathname } = useLocation();
  const pixelId = settings?.meta_pixel_id || "";
  const lastPageViewPath = useRef("");

  useEffect(() => {
    if (!ensurePixelInjected(pixelId)) return;
    // Dedupe PageView per (pathname) so React 18 strict-mode double-invoke
    // in dev doesn't inflate Meta's PageView count. SPA route changes still
    // fire a PageView because pathname actually changes.
    if (lastPageViewPath.current === pathname) return;
    lastPageViewPath.current = pathname;
    if (typeof window !== "undefined" && typeof window.fbq === "function") {
      window.fbq("track", "PageView");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pixelId, pathname]);

  return null;
}

